import { useState, useEffect } from "react";
import { Picker } from "antd-mobile";
import { BRAND } from "../../order-form-tour/types";
import { getTourPriceList, type TourPriceDateGroup } from "@/api/tour";

interface BookingSectionProps {
  routeId: number;
  /** 表单数据变更回调 */
  onChange: (data: BookingFormData) => void;
}

export interface BookingFormData {
  departureDate: string;       // 出团日期 YYYY-MM-DD
  endDate: string;             // 返回日期 YYYY-MM-DD
  priceId: number;             // 选中价格ID
  roomType: string;            // 选中档次
  salePrice: string;           // 选中销售单价
  singleRoomSupplement: string;// 单房差单价
  singleBikeSeat: string;      // 单车位单价
  singleRoomCount: number;     // 单房差数量
  singleBikeCount: number;     // 单车位数量
  peopleCount: number;         // 出游人数
  contactName: string;
  contactPhone: string;
  remark: string;
}

const PEOPLE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function BookingSection({ routeId, onChange }: BookingSectionProps) {
  // 价格列表
  const [dateGroups, setDateGroups] = useState<TourPriceDateGroup[]>([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // 表单字段
  const [selectedDateIdx, setSelectedDateIdx] = useState(-1);
  const [selectedPriceIdx, setSelectedPriceIdx] = useState(-1);
  const [peopleCount, setPeopleCount] = useState(1);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [remark, setRemark] = useState("");
  const [needSingleRoom, setNeedSingleRoom] = useState(false);
  const [needSingleBike, setNeedSingleBike] = useState(false);

  // 加载价格列表
  useEffect(() => {
    if (!routeId) return;
    setPriceLoading(true);
    setSelectedDateIdx(-1);
    setSelectedPriceIdx(-1);
    setNeedSingleRoom(false);
    setNeedSingleBike(false);
    getTourPriceList(routeId)
      .then((res) => {
        if (res.data.code === 200) {
          setDateGroups(res.data.data);
        }
      })
      .finally(() => setPriceLoading(false));
  }, [routeId]);

  // 当前选中的日期段
  const activeGroup = selectedDateIdx >= 0 ? dateGroups[selectedDateIdx] : null;
  // 当前选中的价格项
  const activePrice = selectedDateIdx >= 0 && selectedPriceIdx >= 0
    ? activeGroup?.prices[selectedPriceIdx] ?? null
    : null;

  const emitChange = () => {
    onChange({
      departureDate: activeGroup?.start_date ?? "",
      endDate: activeGroup?.end_date ?? "",
      priceId: activePrice?.id ?? 0,
      roomType: activePrice?.room_type ?? "",
      salePrice: activePrice?.sale_price ?? "",
      singleRoomSupplement: activePrice?.single_room_supplement ?? "",
      singleBikeSeat: activePrice?.single_bike_seat ?? "",
      singleRoomCount: needSingleRoom ? 1 : 0,
      singleBikeCount: needSingleBike ? 1 : 0,
      peopleCount,
      contactName,
      contactPhone,
      remark,
    });
  };

  // 选择日期段
  const handleDateChange = (idx: number) => {
    setSelectedDateIdx(idx);
    setSelectedPriceIdx(-1); // 切换日期段时重置档次
    setNeedSingleRoom(false);
    setNeedSingleBike(false);
    // 不触发 emit，因为还需要选档次
  };

  // 选择档次
  const handlePriceChange = (idx: number) => {
    setSelectedPriceIdx(idx);
    setNeedSingleRoom(false);
    setNeedSingleBike(false);
  };

  // 档次变更后触发回调（使用 useEffect 确保最新值）
  useEffect(() => {
    emitChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateIdx, selectedPriceIdx, needSingleRoom, needSingleBike, peopleCount, contactName, contactPhone, remark]);

  return (
    <div className="bg-white mt-2 px-5 py-4 rounded-t-2xl">
      {/* 标题 */}
      <div className="flex items-center gap-2 mb-4">
        <i className="ri-file-list-3-line text-base" style={{ color: BRAND }} />
        <span className="text-sm font-semibold text-gray-800">填写出行信息</span>
      </div>

      {/* 出团时间段 */}
      <div className="mb-3">
        <label className="text-xs text-gray-500 mb-1.5 block">出团时间段</label>
        <div
          onClick={() => { if (!priceLoading && dateGroups.length > 0) setShowDatePicker(true); }}
          className="w-full px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 flex items-center justify-between outline-none active:border-orange-300 transition-colors"
          style={{ cursor: priceLoading || dateGroups.length === 0 ? "not-allowed" : "pointer" }}
        >
          <span className={selectedDateIdx >= 0 ? "text-gray-700" : "text-gray-400"}>
            {selectedDateIdx >= 0
              ? `${dateGroups[selectedDateIdx].start_date} ~ ${dateGroups[selectedDateIdx].end_date}`
              : (priceLoading ? "加载中..." : "请选择出团时间段")}
          </span>
          <i className="ri-arrow-down-s-line" style={{ color: "#9CA3AF", fontSize: 16 }} />
        </div>
      </div>

      {/* 出团时间段选择器 */}
      <Picker
        columns={[dateGroups.map((group, idx) => ({
          label: `${group.start_date} ~ ${group.end_date}`,
          value: String(idx),
        }))]}
        visible={showDatePicker}
        value={selectedDateIdx >= 0 ? [String(selectedDateIdx)] : []}
        onClose={() => setShowDatePicker(false)}
        onConfirm={(val) => {
          handleDateChange(Number(val[0]));
          setShowDatePicker(false);
        }}
        style={{ "--adm-color-primary": BRAND } as React.CSSProperties}
      />

      {/* 选择档次（级联：选了日期段后出现） */}
      {activeGroup && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1.5 block">选择档次</label>
          {activeGroup.prices.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {activeGroup.prices.map((item, idx) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handlePriceChange(idx)}
                  className="flex flex-col items-center py-2 px-2 rounded-xl text-xs cursor-pointer transition-all"
                  style={{
                    background: selectedPriceIdx === idx ? "#FFF7ED" : "#F9FAFB",
                    border: selectedPriceIdx === idx ? "1.5px solid #F97316" : "1px solid #E5E7EB",
                  }}
                >
                  <span
                    className="font-semibold"
                    style={{ color: selectedPriceIdx === idx ? "#F97316" : "#374151" }}
                  >
                    {item.room_type}
                  </span>
                  <span
                    className="mt-0.5 font-bold"
                    style={{ color: selectedPriceIdx === idx ? "#EF4444" : "#6B7280" }}
                  >
                    ¥{item.sale_price}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-400">该日期段暂无档位</span>
          )}
        </div>
      )}

      {/* 出游人数 + 出发日期 */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs text-gray-500 mb-1.5 block">出游人数</label>
          <select
            value={peopleCount}
            onChange={(e) => { setPeopleCount(Number(e.target.value)); }}
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 text-gray-700 outline-none focus:border-orange-300"
          >
            {PEOPLE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}人</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 block">出发日期</label>
          <input
            type="text"
            readOnly
            value={activeGroup ? activeGroup.start_date : "请先选日期段"}
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 text-gray-500 outline-none cursor-default"
          />
        </div>
      </div>

      {/* 联系人 + 电话 */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs text-gray-500 mb-1.5 block">联系人姓名</label>
          <input
            type="text"
            placeholder="请填写姓名"
            value={contactName}
            onChange={(e) => { setContactName(e.target.value); }}
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 text-gray-700 placeholder:text-gray-400 outline-none focus:border-orange-300"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1.5 block">联系电话</label>
          <input
            type="tel"
            placeholder="手机号码"
            value={contactPhone}
            onChange={(e) => { setContactPhone(e.target.value); }}
            maxLength={11}
            className="w-full px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 text-gray-700 placeholder:text-gray-400 outline-none focus:border-orange-300"
          />
        </div>
      </div>

      {/* 单房差 / 单车位 */}
      {activePrice && (
        <div className="mb-3 space-y-2">
          <label className="text-xs text-gray-500 mb-1.5 block">附加服务</label>
          <label
            className="flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer"
            style={{ background: "#F9FAFB", border: "1px solid #F3F4F6" }}
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={needSingleRoom}
                onChange={(e) => setNeedSingleRoom(e.target.checked)}
                className="w-4 h-4 accent-orange-500 cursor-pointer"
              />
              <span className="text-sm text-gray-700">需要单房差</span>
            </div>
            <span className="text-sm font-bold" style={{ color: "#F97316" }}>
              ¥{activePrice.single_room_supplement}/人
            </span>
          </label>
          <label
            className="flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer"
            style={{ background: "#F9FAFB", border: "1px solid #F3F4F6" }}
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={needSingleBike}
                onChange={(e) => setNeedSingleBike(e.target.checked)}
                className="w-4 h-4 accent-orange-500 cursor-pointer"
              />
              <span className="text-sm text-gray-700">需要单车位</span>
            </div>
            <span className="text-sm font-bold" style={{ color: "#F97316" }}>
              ¥{activePrice.single_bike_seat}/人
            </span>
          </label>
        </div>
      )}

      {/* 价格明细：选择档次后出现 */}
      {activePrice && (
        <div className="mt-4 pt-3" style={{ borderTop: "1px dashed #EDE4D8" }}>
          <div className="rounded-xl p-3 space-y-1.5" style={{ background: "#FFFDF8" }}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">单价（{activePrice.room_type}）</span>
              <span className="font-medium text-gray-800">¥{Number(activePrice.sale_price)}/人</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">×{peopleCount}人</span>
              <span className="font-medium text-gray-800">
                ¥{(Number(activePrice.sale_price) * peopleCount).toLocaleString()}
              </span>
            </div>
            {needSingleRoom && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">单房差</span>
                <span className="font-medium text-gray-800">
                  ¥{Number(activePrice.single_room_supplement).toLocaleString()}
                </span>
              </div>
            )}
            {needSingleBike && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">单车位</span>
                <span className="font-medium text-gray-800">
                  ¥{Number(activePrice.single_bike_seat).toLocaleString()}
                </span>
              </div>
            )}
            <div
              className="flex items-center justify-between pt-1.5"
              style={{ borderTop: "1px dashed #EDE4D8" }}
            >
              <span className="text-sm font-bold" style={{ color: "#F97316" }}>应付金额</span>
              <span className="text-lg font-bold" style={{ color: "#F97316" }}>
                ¥{(
                  Number(activePrice.sale_price) * peopleCount
                  + (needSingleRoom ? Number(activePrice.single_room_supplement) : 0)
                  + (needSingleBike ? Number(activePrice.single_bike_seat) : 0)
                ).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 备注：放在价格明细下方 */}
      <div className="mt-3">
        <label className="text-xs text-gray-500 mb-1.5 block">备注（可选）</label>
        <textarea
          placeholder="特殊要求或备注"
          value={remark}
          onChange={(e) => { setRemark(e.target.value); }}
          rows={2}
          className="w-full px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 text-gray-700 placeholder:text-gray-400 outline-none resize-none focus:border-orange-300"
        />
      </div>
    </div>
  );
}
